import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Lock, 
  LayoutDashboard, 
  Calendar, 
  Mail, 
  Users, 
  Briefcase, 
  ShoppingBag, 
  CreditCard,
  MessageSquare,
  Trash2,
  RefreshCw,
  LogOut,
  Eye,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Tag,
  Percent,
  Download,
  Globe,
  Monitor,
  Smartphone,
  Sun,
  Gift
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge component
const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    new: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    confirmed: 'bg-green-500/20 text-green-400 border-green-500/30',
    completed: 'bg-green-500/20 text-green-400 border-green-500/30',
    contacted: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
    cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
    active: 'bg-green-500/20 text-green-400 border-green-500/30',
    paid: 'bg-green-500/20 text-green-400 border-green-500/30',
  };
  
  const labels = {
    pending: 'In Attesa',
    new: 'Nuovo',
    confirmed: 'Confermato',
    completed: 'Completato',
    contacted: 'Contattato',
    rejected: 'Rifiutato',
    cancelled: 'Annullato',
    active: 'Attivo',
    paid: 'Pagato',
  };

  return (
    <span className={`px-2 py-1 text-xs uppercase tracking-wider border rounded ${styles[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>
      {labels[status] || status}
    </span>
  );
};

// Stat card component
const StatCard = ({ icon: Icon, label, value, color }) => (
  <motion.div
    className="bg-stone-800/50 border border-stone-700 p-6 rounded-lg"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
  >
    <div className="flex items-center gap-4">
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon size={24} />
      </div>
      <div>
        <p className="text-stone-400 text-sm">{label}</p>
        <p className="text-2xl font-bold text-ivory">{value}</p>
      </div>
    </div>
  </motion.div>
);

// Login form
const LoginForm = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      if (response.ok) {
        onLogin(password);
        toast.success('Accesso effettuato');
      } else {
        toast.error('Password non valida');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-night flex items-center justify-center p-6">
      <motion.div
        className="w-full max-w-md"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-champagne/20 rounded-full mb-4">
            <Lock className="text-champagne" size={32} />
          </div>
          <h1 className="font-heading text-3xl text-ivory">Admin Dashboard</h1>
          <p className="text-stone-400 mt-2">La Villa - Pannello di Gestione</p>
        </div>
        
        <form onSubmit={handleSubmit} className="bg-stone-900/50 border border-stone-800 p-8 rounded-lg">
          <div className="mb-6">
            <Label htmlFor="password" className="text-stone-300">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Inserisci la password"
              className="mt-2 bg-stone-800 border-stone-700 text-ivory"
              data-testid="admin-password"
            />
          </div>
          
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-champagne text-night hover:bg-ivory"
            data-testid="admin-login-btn"
          >
            {loading ? 'Accesso...' : 'Accedi'}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

// Main Dashboard
export default function AdminDashboard() {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({
    bookings: [],
    newsletter: [],
    priorityList: [],
    workApplications: [],
    shopOrders: [],
    subscriptions: [],
    contacts: [],
    estate2027: [],
    giftcards: [],
    giftcardsIssued: [],
    coupons: { coupons: [], stats: {} },
    visitors: { visitors: [], stats: {} }
  });
  const [loading, setLoading] = useState(false);
  const [generatingCoupons, setGeneratingCoupons] = useState(false);

  const tabs = [
    { id: 'overview', label: 'Panoramica', icon: LayoutDashboard },
    { id: 'visitors', label: 'Visitatori', icon: Globe },
    { id: 'bookings', label: 'Prenotazioni', icon: Calendar },
    { id: 'newsletter', label: 'Newsletter', icon: Mail },
    { id: 'estate2027', label: 'Estate 2027', icon: Sun },
    { id: 'giftcards', label: 'Gift Card', icon: Gift },
    { id: 'priority', label: 'Lista Prioritaria', icon: Users },
    { id: 'work', label: 'Candidature', icon: Briefcase },
    { id: 'orders', label: 'Ordini Shop', icon: ShoppingBag },
    { id: 'subscriptions', label: 'Abbonamenti', icon: CreditCard },
    { id: 'contacts', label: 'Contatti', icon: MessageSquare },
    { id: 'coupons', label: 'Coupon', icon: Tag },
  ];

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats?password=${password}`);
      if (response.ok) {
        const result = await response.json();
        setStats(result);
      }
    } catch {
      toast.error('Errore nel caricamento delle statistiche');
    }
  }, [password]);

  const fetchData = useCallback(async (endpoint, key) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/${endpoint}?password=${password}`);
      if (response.ok) {
        const result = await response.json();
        setData(prev => ({ ...prev, [key]: result }));
      }
    } catch {
      toast.error('Errore nel caricamento dei dati');
    }
    setLoading(false);
  }, [password]);

  const updateStatus = async (endpoint, id, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/${endpoint}/${id}/status?password=${password}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        toast.success('Stato aggiornato');
        // Refresh data
        if (endpoint === 'event-bookings') fetchData('event-bookings', 'bookings');
        if (endpoint === 'work-applications') fetchData('work-applications', 'workApplications');
        if (endpoint === 'contacts') fetchData('contacts', 'contacts');
      }
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const deleteItem = async (endpoint, id, key) => {
    if (!confirm('Sei sicuro di voler eliminare questo elemento?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/${endpoint}/${id}?password=${password}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Elemento eliminato');
        setData(prev => ({
          ...prev,
          [key]: prev[key].filter(item => item.id !== id)
        }));
      }
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/coupons?password=${password}`);
      if (response.ok) {
        const result = await response.json();
        setData(prev => ({ ...prev, coupons: result }));
      }
    } catch {
      toast.error('Errore nel caricamento dei coupon');
    }
    setLoading(false);
  }, [password]);

  const generateCoupons = async () => {
    if (!confirm('Vuoi generare 100 nuovi coupon (50 al 50%, 50 al 30%)? Assicurati che non esistano già coupon.')) return;
    
    setGeneratingCoupons(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/generate-coupons?password=${password}`, {
        method: 'POST'
      });
      if (response.ok) {
        toast.success('100 coupon generati con successo!');
        fetchCoupons();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Errore nella generazione');
      }
    } catch (error) {
      toast.error('Errore nella generazione dei coupon');
    }
    setGeneratingCoupons(false);
  };

  const resetCoupons = async () => {
    if (!confirm('ATTENZIONE: Vuoi eliminare TUTTI i coupon? Questa azione è irreversibile!')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/coupons/reset?password=${password}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Tutti i coupon sono stati eliminati');
        setData(prev => ({ ...prev, coupons: { coupons: [], stats: {} } }));
      }
    } catch (error) {
      toast.error('Errore nell\'eliminazione dei coupon');
    }
  };

  const exportCoupons = () => {
    const coupons = data.coupons.coupons || [];
    const csvContent = [
      'Codice,Sconto %,Usato,Usato Da,Data Utilizzo',
      ...coupons.map(c => 
        `${c.code},${c.discount_percent}%,${c.used ? 'Sì' : 'No'},${c.used_by || ''},${c.used_at || ''}`
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `coupon_lavilla_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const fetchVisitors = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/visitors?password=${password}&page=${page}&limit=100`);
      if (response.ok) {
        const result = await response.json();
        setData(prev => ({ ...prev, visitors: result }));
      }
    } catch {
      toast.error('Errore nel caricamento dei visitatori');
    }
    setLoading(false);
  }, [password]);

  const clearVisitors = async () => {
    if (!confirm('ATTENZIONE: Vuoi eliminare TUTTI i log dei visitatori? Questa azione è irreversibile!')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/visitors/clear?password=${password}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Log visitatori eliminati');
        setData(prev => ({ ...prev, visitors: { visitors: [], stats: {} } }));
      }
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const exportVisitors = () => {
    const visitors = data.visitors.visitors || [];
    const csvContent = [
      'IP,Città,Regione,Paese,Pagina,Referrer,User Agent,Data/Ora',
      ...visitors.map(v => 
        `"${v.ip_address}","${v.city || ''}","${v.region || ''}","${v.country || ''}","${v.page}","${v.referrer || ''}","${v.user_agent}","${v.visited_at}"`
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `visitatori_lavilla_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportEstate2027 = () => {
    const leads = data.estate2027 || [];
    const csvContent = [
      'Nome,Email,Interessi,Data Iscrizione,Ultimo Aggiornamento',
      ...leads.map(l =>
        `"${l.name}","${l.email}","${(l.interests || []).join(' | ')}","${l.created_at || ''}","${l.updated_at || ''}"`
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `estate2027_leads_lavilla_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportGiftcards = () => {
    const reqs = data.giftcards || [];
    const csvContent = [
      'Data,Taglio,Bonus Aperitivo,Acquirente,Email,Destinatario,Dedica,Stato',
      ...reqs.map(r =>
        `"${r.created_at || ''}","${r.amount} €","${r.bonus_aperitivo ? r.bonus_aperitivo + ' €' : '—'}","${r.buyer_name}","${r.buyer_email}","${r.recipient_name || ''}","${(r.dedication || '').replace(/"/g, '""')}","${r.status}"`
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `giftcard_richieste_lavilla_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const redeemGiftcard = async (giftId, wallet, walletLabel) => {
    const value = window.prompt(`Importo da scalare dal ${walletLabel} (in €):`);
    if (!value) return;
    const amount = parseFloat(value.replace(',', '.'));
    if (isNaN(amount) || amount <= 0) {
      toast.error('Importo non valido');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/admin/giftcards/${giftId}/redeem?password=${password}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, wallet })
      });
      if (response.ok) {
        toast.success('Importo scalato');
        fetchData('giftcards', 'giftcardsIssued');
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Errore nella scalatura');
      }
    } catch {
      toast.error('Errore di connessione');
    }
  };

  const exportGiftcardsIssued = () => {
    const cards = data.giftcardsIssued || [];
    const csvContent = [
      'Codice,Valore,Credito Residuo,Bonus,Bonus Residuo,Acquirente,Email,Destinatario,Stato,Scadenza,Data Acquisto',
      ...cards.map(g =>
        `"${g.code}","${g.amount} €","${(g.credit_balance_cents / 100).toFixed(2)} €","${g.bonus_aperitivo} €","${(g.bonus_balance_cents / 100).toFixed(2)} €","${g.buyer_name}","${g.buyer_email}","${g.recipient_name || ''}","${g.status}","${g.valid_until}","${g.created_at || ''}"`
      )
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `giftcard_emesse_lavilla_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const getDeviceIcon = (userAgent) => {
    if (!userAgent) return <Monitor size={16} />;
    const ua = userAgent.toLowerCase();
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
      return <Smartphone size={16} className="text-blue-400" />;
    }
    return <Monitor size={16} className="text-stone-400" />;
  };

  const getBrowserName = (userAgent) => {
    if (!userAgent) return 'Sconosciuto';
    const ua = userAgent.toLowerCase();
    if (ua.includes('chrome') && !ua.includes('edg')) return 'Chrome';
    if (ua.includes('firefox')) return 'Firefox';
    if (ua.includes('safari') && !ua.includes('chrome')) return 'Safari';
    if (ua.includes('edg')) return 'Edge';
    if (ua.includes('opera') || ua.includes('opr')) return 'Opera';
    return 'Altro';
  };

  useEffect(() => {
    if (authenticated && password) {
      fetchStats();
    }
  }, [authenticated, password, fetchStats]);

  useEffect(() => {
    if (authenticated && password) {
      switch (activeTab) {
        case 'visitors': fetchVisitors(); break;
        case 'bookings': fetchData('event-bookings', 'bookings'); break;
        case 'newsletter': fetchData('newsletter', 'newsletter'); break;
        case 'estate2027': fetchData('estate2027-leads', 'estate2027'); break;
        case 'giftcards':
          fetchData('giftcard-requests', 'giftcards');
          fetchData('giftcards', 'giftcardsIssued');
          break;
        case 'priority': fetchData('priority-list', 'priorityList'); break;
        case 'work': fetchData('work-applications', 'workApplications'); break;
        case 'orders': fetchData('shop-orders', 'shopOrders'); break;
        case 'subscriptions': fetchData('subscriptions', 'subscriptions'); break;
        case 'contacts': fetchData('contacts', 'contacts'); break;
        case 'coupons': fetchCoupons(); break;
      }
    }
  }, [activeTab, authenticated, password, fetchVisitors, fetchData, fetchCoupons]);

  const handleLogin = (pwd) => {
    setPassword(pwd);
    setAuthenticated(true);
  };

  const handleLogout = () => {
    setAuthenticated(false);
    setPassword('');
    setStats(null);
    setData({
      bookings: [],
      newsletter: [],
      priorityList: [],
      workApplications: [],
      shopOrders: [],
      subscriptions: [],
      contacts: [],
      coupons: { coupons: [], stats: {} },
      visitors: { visitors: [], stats: {} }
    });
  };

  if (!authenticated) {
    return <LoginForm onLogin={handleLogin} />;
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-night" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-stone-900/80 border-b border-stone-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <LayoutDashboard className="text-champagne" size={28} />
            <div>
              <h1 className="font-heading text-xl text-ivory">Admin Dashboard</h1>
              <p className="text-stone-500 text-sm">La Villa - Pannello di Gestione</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="border-stone-700 text-stone-300 hover:bg-stone-800"
          >
            <LogOut size={16} className="mr-2" />
            Esci
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 pb-4 border-b border-stone-800">
          {tabs.map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id 
                ? 'bg-champagne text-night hover:bg-ivory' 
                : 'border-stone-700 text-stone-300 hover:bg-stone-800'
              }
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={16} className="mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'overview' && stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              icon={Calendar} 
              label="Prenotazioni Eventi" 
              value={stats.event_bookings} 
              color="bg-blue-500/20 text-blue-400"
            />
            <StatCard 
              icon={Mail} 
              label="Iscritti Newsletter" 
              value={stats.newsletter_subscribers} 
              color="bg-green-500/20 text-green-400"
            />
            <StatCard 
              icon={Users} 
              label="Lista Prioritaria" 
              value={stats.priority_list} 
              color="bg-purple-500/20 text-purple-400"
            />
            <StatCard 
              icon={Briefcase} 
              label="Candidature Lavoro" 
              value={stats.work_applications} 
              color="bg-orange-500/20 text-orange-400"
            />
            <StatCard 
              icon={ShoppingBag} 
              label="Ordini Shop" 
              value={stats.shop_orders} 
              color="bg-pink-500/20 text-pink-400"
            />
            <StatCard 
              icon={CreditCard} 
              label="Abbonamenti" 
              value={stats.subscriptions} 
              color="bg-cyan-500/20 text-cyan-400"
            />
            <StatCard 
              icon={AlertCircle} 
              label="Nuove Prenotazioni" 
              value={stats.new_bookings} 
              color="bg-yellow-500/20 text-yellow-400"
            />
            <StatCard 
              icon={AlertCircle} 
              label="Nuove Candidature" 
              value={stats.new_applications} 
              color="bg-red-500/20 text-red-400"
            />
          </div>
        )}

        {/* Visitatori */}
        {activeTab === 'visitors' && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <h2 className="font-heading text-2xl text-ivory">Log Visitatori</h2>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => fetchVisitors()}
                  className="border-stone-700 text-stone-300"
                >
                  <RefreshCw size={16} className="mr-2" />
                  Aggiorna
                </Button>
                <Button
                  variant="outline"
                  onClick={exportVisitors}
                  className="border-stone-700 text-stone-300"
                  disabled={!data.visitors.visitors?.length}
                >
                  <Download size={16} className="mr-2" />
                  Esporta CSV
                </Button>
                <Button
                  data-testid="download-analytics-pdf-btn"
                  onClick={() => window.open(`${API_URL}/api/admin/analytics/report?password=${encodeURIComponent(password)}`, '_blank')}
                  className="bg-champagne text-charcoal hover:bg-champagne/90"
                  disabled={!data.visitors.stats?.total_visits}
                >
                  <Download size={16} className="mr-2" />
                  Report PDF Investitori
                </Button>
                <Button
                  onClick={clearVisitors}
                  variant="destructive"
                  disabled={!data.visitors.stats?.total_visits}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Trash2 size={16} className="mr-2" />
                  Cancella Tutti
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            {data.visitors.stats && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-stone-900/50 border border-stone-800 p-4 rounded-lg text-center">
                  <p className="text-stone-400 text-sm">Visite Totali</p>
                  <p className="text-3xl font-bold text-ivory">{data.visitors.stats.total_visits || 0}</p>
                </div>
                <div className="bg-green-500/10 border border-green-500/30 p-4 rounded-lg text-center">
                  <p className="text-green-400 text-sm">IP Unici</p>
                  <p className="text-3xl font-bold text-green-400">{data.visitors.stats.unique_ips || 0}</p>
                </div>
                <div className="bg-champagne/10 border border-champagne/30 p-4 rounded-lg text-center">
                  <p className="text-champagne text-sm">Visite Oggi</p>
                  <p className="text-3xl font-bold text-champagne">{data.visitors.stats.today_visits || 0}</p>
                </div>
              </div>
            )}

            {/* Visitors Table */}
            {loading ? (
              <p className="text-stone-400">Caricamento...</p>
            ) : data.visitors.visitors?.length === 0 ? (
              <div className="bg-stone-900/50 border border-stone-800 p-12 rounded-lg text-center">
                <Globe size={48} className="mx-auto text-stone-600 mb-4" />
                <p className="text-stone-400 mb-4">Nessun visitatore registrato</p>
                <p className="text-stone-500 text-sm">
                  I visitatori verranno tracciati automaticamente quando navigano il sito.
                </p>
              </div>
            ) : (
              <div className="bg-stone-900/50 border border-stone-800 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-stone-800/50">
                      <tr>
                        <th className="text-left p-4 text-stone-400 font-normal">IP</th>
                        <th className="text-left p-4 text-stone-400 font-normal">Posizione</th>
                        <th className="text-left p-4 text-stone-400 font-normal">Pagina</th>
                        <th className="text-left p-4 text-stone-400 font-normal">Dispositivo</th>
                        <th className="text-left p-4 text-stone-400 font-normal">Browser</th>
                        <th className="text-left p-4 text-stone-400 font-normal">Data/Ora</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.visitors.visitors.map((visitor, index) => (
                        <tr key={visitor.id || index} className="border-t border-stone-800 hover:bg-stone-800/30">
                          <td className="p-4 text-ivory font-mono text-sm">{visitor.ip_address}</td>
                          <td className="p-4 text-stone-300 text-sm">
                            {visitor.city || visitor.country ? (
                              <span title={`${visitor.region || ''}, ${visitor.country || ''}`}>
                                {visitor.city && <span>{visitor.city}</span>}
                                {visitor.city && visitor.country_code && <span>, </span>}
                                {visitor.country_code && (
                                  <span className="text-champagne font-medium">{visitor.country_code}</span>
                                )}
                              </span>
                            ) : (
                              <span className="text-stone-500">-</span>
                            )}
                          </td>
                          <td className="p-4 text-stone-300">{visitor.page}</td>
                          <td className="p-4">
                            {getDeviceIcon(visitor.user_agent)}
                          </td>
                          <td className="p-4 text-stone-300 text-sm">{getBrowserName(visitor.user_agent)}</td>
                          <td className="p-4 text-stone-400 text-sm">{formatDate(visitor.visited_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {data.visitors.stats?.total_pages > 1 && (
                  <div className="flex items-center justify-center gap-2 p-4 border-t border-stone-800">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchVisitors(data.visitors.stats.page - 1)}
                      disabled={data.visitors.stats.page <= 1}
                      className="border-stone-700 text-stone-300"
                    >
                      Precedente
                    </Button>
                    <span className="text-stone-400 text-sm">
                      Pagina {data.visitors.stats.page} di {data.visitors.stats.total_pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchVisitors(data.visitors.stats.page + 1)}
                      disabled={data.visitors.stats.page >= data.visitors.stats.total_pages}
                      className="border-stone-700 text-stone-300"
                    >
                      Successiva
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Prenotazioni Eventi */}
        {activeTab === 'bookings' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl text-ivory">Prenotazioni Eventi Privati</h2>
              <Button
                variant="outline"
                onClick={() => fetchData('event-bookings', 'bookings')}
                className="border-stone-700 text-stone-300"
              >
                <RefreshCw size={16} className="mr-2" />
                Aggiorna
              </Button>
            </div>
            
            {loading ? (
              <p className="text-stone-400">Caricamento...</p>
            ) : data.bookings.length === 0 ? (
              <p className="text-stone-400">Nessuna prenotazione</p>
            ) : (
              <div className="space-y-4">
                {data.bookings.map(booking => (
                  <div key={booking.id} className="bg-stone-900/50 border border-stone-800 p-6 rounded-lg">
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                      <div>
                        <h3 className="font-heading text-xl text-ivory">{booking.event_type}</h3>
                        <p className="text-champagne">{booking.date} - {booking.time_slot}</p>
                      </div>
                      <StatusBadge status={booking.status} />
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4 mb-4 text-sm">
                      <div>
                        <p className="text-stone-400">Contatto</p>
                        <p className="text-ivory">{booking.contact?.name}</p>
                        <p className="text-stone-300">{booking.contact?.email}</p>
                        <p className="text-stone-300">{booking.contact?.phone}</p>
                      </div>
                      <div>
                        <p className="text-stone-400">Dettagli</p>
                        <p className="text-ivory">Ospiti: {booking.guests}</p>
                        <p className="text-stone-300">Pacchetto: {booking.package}</p>
                        <p className="text-stone-300">Preferenza: {booking.contact?.preference}</p>
                      </div>
                    </div>
                    
                    {booking.notes && (
                      <div className="mb-4 p-3 bg-stone-800/50 rounded">
                        <p className="text-stone-400 text-sm">Note:</p>
                        <p className="text-stone-300">{booking.notes}</p>
                      </div>
                    )}
                    
                    <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-stone-800">
                      <Select 
                        value={booking.status}
                        onValueChange={(value) => updateStatus('event-bookings', booking.id, value)}
                      >
                        <SelectTrigger className="w-40 bg-stone-800 border-stone-700 text-ivory">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-stone-800 border-stone-700">
                          <SelectItem value="pending" className="text-ivory">In Attesa</SelectItem>
                          <SelectItem value="contacted" className="text-ivory">Contattato</SelectItem>
                          <SelectItem value="confirmed" className="text-ivory">Confermato</SelectItem>
                          <SelectItem value="completed" className="text-ivory">Completato</SelectItem>
                          <SelectItem value="cancelled" className="text-ivory">Annullato</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="text-stone-500 text-sm">
                        Ricevuto: {formatDate(booking.created_at)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Newsletter */}
        {activeTab === 'newsletter' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl text-ivory">Iscritti Newsletter ({data.newsletter.length})</h2>
              <Button
                variant="outline"
                onClick={() => fetchData('newsletter', 'newsletter')}
                className="border-stone-700 text-stone-300"
              >
                <RefreshCw size={16} className="mr-2" />
                Aggiorna
              </Button>
            </div>
            
            <div className="bg-stone-900/50 border border-stone-800 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-stone-800/50">
                  <tr>
                    <th className="text-left p-4 text-stone-400 font-normal">Email</th>
                    <th className="text-left p-4 text-stone-400 font-normal">Data Iscrizione</th>
                    <th className="text-left p-4 text-stone-400 font-normal">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {data.newsletter.map(sub => (
                    <tr key={sub.id} className="border-t border-stone-800">
                      <td className="p-4 text-ivory">{sub.email}</td>
                      <td className="p-4 text-stone-300">{formatDate(sub.subscribed_at)}</td>
                      <td className="p-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteItem('newsletter', sub.id, 'newsletter')}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Estate 2027 Leads */}
        {activeTab === 'estate2027' && (
          <div>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="font-heading text-2xl text-ivory">Iscrizioni Estate 2027 ({data.estate2027.length})</h2>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => fetchData('estate2027-leads', 'estate2027')}
                  className="border-stone-700 text-stone-300"
                  data-testid="estate2027-refresh-btn"
                >
                  <RefreshCw size={16} className="mr-2" />
                  Aggiorna
                </Button>
                <Button
                  onClick={exportEstate2027}
                  className="bg-champagne text-charcoal hover:bg-champagne/90"
                  disabled={!data.estate2027.length}
                  data-testid="estate2027-export-csv-btn"
                >
                  <Download size={16} className="mr-2" />
                  Esporta CSV
                </Button>
              </div>
            </div>

            <div className="bg-stone-900/50 border border-stone-800 rounded-lg overflow-x-auto" data-testid="estate2027-leads-table">
              <table className="w-full">
                <thead className="bg-stone-800/50">
                  <tr>
                    <th className="text-left p-4 text-stone-400 font-normal">Nome</th>
                    <th className="text-left p-4 text-stone-400 font-normal">Email</th>
                    <th className="text-left p-4 text-stone-400 font-normal">Interessi</th>
                    <th className="text-left p-4 text-stone-400 font-normal">Data</th>
                    <th className="text-left p-4 text-stone-400 font-normal">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {data.estate2027.map(lead => (
                    <tr key={lead.id} className="border-t border-stone-800">
                      <td className="p-4 text-ivory">{lead.name}</td>
                      <td className="p-4 text-stone-300">{lead.email}</td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1.5">
                          {(lead.interests || []).map(i => (
                            <span key={i} className="text-xs bg-champagne/15 text-champagne px-2 py-0.5 rounded-full whitespace-nowrap">
                              {i}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="p-4 text-stone-300 whitespace-nowrap">{formatDate(lead.created_at)}</td>
                      <td className="p-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteItem('estate2027-leads', lead.id, 'estate2027')}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!data.estate2027.length && (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-stone-500 font-body">
                        Nessuna iscrizione ancora. I contatti raccolti dal form di /estate-2027 appariranno qui.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Gift Card Requests */}
        {activeTab === 'giftcards' && (
          <div>
            {/* Buoni emessi */}
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="font-heading text-2xl text-ivory">Buoni Emessi ({data.giftcardsIssued.length})</h2>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => fetchData('giftcards', 'giftcardsIssued')}
                  className="border-stone-700 text-stone-300"
                  data-testid="giftcards-issued-refresh-btn"
                >
                  <RefreshCw size={16} className="mr-2" />
                  Aggiorna
                </Button>
                <Button
                  onClick={exportGiftcardsIssued}
                  className="bg-champagne text-charcoal hover:bg-champagne/90"
                  disabled={!data.giftcardsIssued.length}
                  data-testid="giftcards-issued-export-btn"
                >
                  <Download size={16} className="mr-2" />
                  Esporta CSV
                </Button>
              </div>
            </div>

            <div className="bg-stone-900/50 border border-stone-800 rounded-lg overflow-x-auto mb-10" data-testid="giftcards-issued-table">
              <table className="w-full">
                <thead className="bg-stone-800/50">
                  <tr>
                    <th className="text-left p-4 text-stone-400 font-normal">Codice</th>
                    <th className="text-left p-4 text-stone-400 font-normal">Credito residuo</th>
                    <th className="text-left p-4 text-stone-400 font-normal">Bonus residuo</th>
                    <th className="text-left p-4 text-stone-400 font-normal">Acquirente</th>
                    <th className="text-left p-4 text-stone-400 font-normal">Data</th>
                    <th className="text-left p-4 text-stone-400 font-normal">Scala importo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.giftcardsIssued.map(g => (
                    <tr key={g.id} className="border-t border-stone-800">
                      <td className="p-4">
                        <p className="font-mono text-champagne tracking-wider">{g.code}</p>
                        <p className="text-stone-500 text-xs">Valore {g.amount} € {g.bonus_aperitivo ? `+ ${g.bonus_aperitivo} € aperitivo` : ''}</p>
                      </td>
                      <td className="p-4">
                        <span className="text-ivory font-medium">{(g.credit_balance_cents / 100).toFixed(2)} €</span>
                      </td>
                      <td className="p-4">
                        {g.bonus_aperitivo ? (
                          <span className="text-champagne">{(g.bonus_balance_cents / 100).toFixed(2)} €</span>
                        ) : <span className="text-stone-600">—</span>}
                      </td>
                      <td className="p-4">
                        <p className="text-ivory text-sm">{g.buyer_name}</p>
                        <p className="text-stone-400 text-xs">{g.buyer_email}</p>
                      </td>
                      <td className="p-4 text-stone-300 whitespace-nowrap text-sm">{formatDate(g.created_at)}</td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => redeemGiftcard(g.id, 'credit', 'credito')}
                            className="border-stone-700 text-stone-300 text-xs"
                            data-testid={`redeem-credit-${g.code}`}
                          >
                            − Credito
                          </Button>
                          {g.bonus_aperitivo > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => redeemGiftcard(g.id, 'bonus', 'bonus aperitivo')}
                              className="border-champagne/40 text-champagne text-xs"
                              data-testid={`redeem-bonus-${g.code}`}
                            >
                              − Bonus
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!data.giftcardsIssued.length && (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-stone-500 font-body">
                        Nessun buono emesso ancora. I buoni acquistati online (Nexi) appariranno qui con codice e saldi.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="font-heading text-2xl text-ivory">Richieste Gift Card ({data.giftcards.length})</h2>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => fetchData('giftcard-requests', 'giftcards')}
                  className="border-stone-700 text-stone-300"
                  data-testid="giftcards-refresh-btn"
                >
                  <RefreshCw size={16} className="mr-2" />
                  Aggiorna
                </Button>
                <Button
                  onClick={exportGiftcards}
                  className="bg-champagne text-charcoal hover:bg-champagne/90"
                  disabled={!data.giftcards.length}
                  data-testid="giftcards-export-csv-btn"
                >
                  <Download size={16} className="mr-2" />
                  Esporta CSV
                </Button>
              </div>
            </div>

            <div className="bg-stone-900/50 border border-stone-800 rounded-lg overflow-x-auto" data-testid="giftcards-table">
              <table className="w-full">
                <thead className="bg-stone-800/50">
                  <tr>
                    <th className="text-left p-4 text-stone-400 font-normal">Taglio</th>
                    <th className="text-left p-4 text-stone-400 font-normal">Bonus</th>
                    <th className="text-left p-4 text-stone-400 font-normal">Acquirente</th>
                    <th className="text-left p-4 text-stone-400 font-normal">Destinatario / Dedica</th>
                    <th className="text-left p-4 text-stone-400 font-normal">Data</th>
                    <th className="text-left p-4 text-stone-400 font-normal">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {data.giftcards.map(r => (
                    <tr key={r.id} className="border-t border-stone-800">
                      <td className="p-4">
                        <span className="font-heading text-lg text-champagne">{r.amount} €</span>
                      </td>
                      <td className="p-4 text-stone-300 whitespace-nowrap">
                        {r.bonus_aperitivo ? (
                          <span className="text-xs bg-champagne/15 text-champagne px-2 py-0.5 rounded-full">+{r.bonus_aperitivo} € aperitivo</span>
                        ) : '—'}
                      </td>
                      <td className="p-4">
                        <p className="text-ivory">{r.buyer_name}</p>
                        <p className="text-stone-400 text-sm">{r.buyer_email}</p>
                      </td>
                      <td className="p-4 max-w-[280px]">
                        {r.recipient_name && <p className="text-stone-300 text-sm">Per: {r.recipient_name}</p>}
                        {r.dedication && <p className="text-stone-500 text-xs italic truncate">"{r.dedication}"</p>}
                        {!r.recipient_name && !r.dedication && <span className="text-stone-600">—</span>}
                      </td>
                      <td className="p-4 text-stone-300 whitespace-nowrap">{formatDate(r.created_at)}</td>
                      <td className="p-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteItem('giftcard-requests', r.id, 'giftcards')}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!data.giftcards.length && (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-stone-500 font-body">
                        Nessuna richiesta ancora. Le richieste Gift Card dalla pagina Abbonamenti appariranno qui.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Lista Prioritaria */}
        {activeTab === 'priority' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl text-ivory">Lista Prioritaria ({data.priorityList.length})</h2>
              <Button
                variant="outline"
                onClick={() => fetchData('priority-list', 'priorityList')}
                className="border-stone-700 text-stone-300"
              >
                <RefreshCw size={16} className="mr-2" />
                Aggiorna
              </Button>
            </div>
            
            <div className="bg-stone-900/50 border border-stone-800 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-stone-800/50">
                  <tr>
                    <th className="text-left p-4 text-stone-400 font-normal">Nome</th>
                    <th className="text-left p-4 text-stone-400 font-normal">Contatto</th>
                    <th className="text-left p-4 text-stone-400 font-normal">Preferenze</th>
                    <th className="text-left p-4 text-stone-400 font-normal">Data</th>
                    <th className="text-left p-4 text-stone-400 font-normal">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {data.priorityList.map(sub => (
                    <tr key={sub.id} className="border-t border-stone-800">
                      <td className="p-4 text-ivory">{sub.name}</td>
                      <td className="p-4 text-stone-300">{sub.contact}</td>
                      <td className="p-4 text-stone-300">{sub.preferences?.join(', ') || '-'}</td>
                      <td className="p-4 text-stone-300">{formatDate(sub.subscribed_at)}</td>
                      <td className="p-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteItem('priority-list', sub.id, 'priorityList')}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Candidature Lavoro */}
        {activeTab === 'work' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl text-ivory">Candidature Lavoro ({data.workApplications.length})</h2>
              <Button
                variant="outline"
                onClick={() => fetchData('work-applications', 'workApplications')}
                className="border-stone-700 text-stone-300"
              >
                <RefreshCw size={16} className="mr-2" />
                Aggiorna
              </Button>
            </div>
            
            <div className="space-y-4">
              {data.workApplications.map(app => (
                <div key={app.id} className="bg-stone-900/50 border border-stone-800 p-6 rounded-lg">
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                    <div>
                      <h3 className="font-heading text-xl text-ivory">{app.name}</h3>
                      <p className="text-champagne">{app.role}</p>
                      <p className="text-stone-300">{app.contact}</p>
                    </div>
                    <StatusBadge status={app.status} />
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-stone-800">
                    <Select 
                      value={app.status}
                      onValueChange={(value) => updateStatus('work-applications', app.id, value)}
                    >
                      <SelectTrigger className="w-40 bg-stone-800 border-stone-700 text-ivory">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-stone-800 border-stone-700">
                        <SelectItem value="new" className="text-ivory">Nuovo</SelectItem>
                        <SelectItem value="contacted" className="text-ivory">Contattato</SelectItem>
                        <SelectItem value="confirmed" className="text-ivory">Assunto</SelectItem>
                        <SelectItem value="rejected" className="text-ivory">Rifiutato</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteItem('work-applications', app.id, 'workApplications')}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 size={16} className="mr-1" />
                      Elimina
                    </Button>
                    <span className="text-stone-500 text-sm ml-auto">
                      Ricevuto: {formatDate(app.applied_at)}
                    </span>
                  </div>
                </div>
              ))}
              {data.workApplications.length === 0 && (
                <p className="text-stone-400">Nessuna candidatura</p>
              )}
            </div>
          </div>
        )}

        {/* Ordini Shop */}
        {activeTab === 'orders' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl text-ivory">Ordini Shop ({data.shopOrders.length})</h2>
              <Button
                variant="outline"
                onClick={() => fetchData('shop-orders', 'shopOrders')}
                className="border-stone-700 text-stone-300"
              >
                <RefreshCw size={16} className="mr-2" />
                Aggiorna
              </Button>
            </div>
            
            {data.shopOrders.length === 0 ? (
              <p className="text-stone-400">Nessun ordine (i pagamenti sono disabilitati)</p>
            ) : (
              <div className="bg-stone-900/50 border border-stone-800 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-stone-800/50">
                    <tr>
                      <th className="text-left p-4 text-stone-400 font-normal">ID</th>
                      <th className="text-left p-4 text-stone-400 font-normal">Email</th>
                      <th className="text-left p-4 text-stone-400 font-normal">Importo</th>
                      <th className="text-left p-4 text-stone-400 font-normal">Stato</th>
                      <th className="text-left p-4 text-stone-400 font-normal">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.shopOrders.map(order => (
                      <tr key={order.id} className="border-t border-stone-800">
                        <td className="p-4 text-ivory font-mono text-sm">{order.id?.slice(0, 8)}...</td>
                        <td className="p-4 text-stone-300">{order.customer_email}</td>
                        <td className="p-4 text-champagne font-semibold">€{order.amount}</td>
                        <td className="p-4"><StatusBadge status={order.payment_status} /></td>
                        <td className="p-4 text-stone-300">{formatDate(order.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Abbonamenti */}
        {activeTab === 'subscriptions' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl text-ivory">Abbonamenti ({data.subscriptions.length})</h2>
              <Button
                variant="outline"
                onClick={() => fetchData('subscriptions', 'subscriptions')}
                className="border-stone-700 text-stone-300"
              >
                <RefreshCw size={16} className="mr-2" />
                Aggiorna
              </Button>
            </div>
            
            {data.subscriptions.length === 0 ? (
              <p className="text-stone-400">Nessun abbonamento (i pagamenti sono disabilitati)</p>
            ) : (
              <div className="bg-stone-900/50 border border-stone-800 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-stone-800/50">
                    <tr>
                      <th className="text-left p-4 text-stone-400 font-normal">Pacchetto</th>
                      <th className="text-left p-4 text-stone-400 font-normal">Email</th>
                      <th className="text-left p-4 text-stone-400 font-normal">Importo</th>
                      <th className="text-left p-4 text-stone-400 font-normal">Stato</th>
                      <th className="text-left p-4 text-stone-400 font-normal">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.subscriptions.map(sub => (
                      <tr key={sub.id} className="border-t border-stone-800">
                        <td className="p-4 text-ivory">{sub.package_name}</td>
                        <td className="p-4 text-stone-300">{sub.customer_email}</td>
                        <td className="p-4 text-champagne font-semibold">€{sub.amount}</td>
                        <td className="p-4"><StatusBadge status={sub.payment_status} /></td>
                        <td className="p-4 text-stone-300">{formatDate(sub.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Contatti */}
        {activeTab === 'contacts' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl text-ivory">Messaggi Contatti ({data.contacts.length})</h2>
              <Button
                variant="outline"
                onClick={() => fetchData('contacts', 'contacts')}
                className="border-stone-700 text-stone-300"
              >
                <RefreshCw size={16} className="mr-2" />
                Aggiorna
              </Button>
            </div>
            
            <div className="space-y-4">
              {data.contacts.map(contact => (
                <div key={contact.id} className="bg-stone-900/50 border border-stone-800 p-6 rounded-lg">
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                    <div>
                      <h3 className="font-heading text-xl text-ivory">{contact.name}</h3>
                      <p className="text-champagne">{contact.email}</p>
                      {contact.phone && <p className="text-stone-300">{contact.phone}</p>}
                    </div>
                    <StatusBadge status={contact.status} />
                  </div>
                  
                  <div className="mb-4 p-3 bg-stone-800/50 rounded">
                    <p className="text-stone-300">{contact.message}</p>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-stone-800">
                    <Select 
                      value={contact.status}
                      onValueChange={(value) => updateStatus('contacts', contact.id, value)}
                    >
                      <SelectTrigger className="w-40 bg-stone-800 border-stone-700 text-ivory">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-stone-800 border-stone-700">
                        <SelectItem value="new" className="text-ivory">Nuovo</SelectItem>
                        <SelectItem value="contacted" className="text-ivory">Risposto</SelectItem>
                        <SelectItem value="completed" className="text-ivory">Risolto</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-stone-500 text-sm ml-auto">
                      Ricevuto: {formatDate(contact.created_at)}
                    </span>
                  </div>
                </div>
              ))}
              {data.contacts.length === 0 && (
                <p className="text-stone-400">Nessun messaggio</p>
              )}
            </div>
          </div>
        )}

        {/* Coupon */}
        {activeTab === 'coupons' && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <h2 className="font-heading text-2xl text-ivory">Gestione Coupon</h2>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={fetchCoupons}
                  className="border-stone-700 text-stone-300"
                >
                  <RefreshCw size={16} className="mr-2" />
                  Aggiorna
                </Button>
                <Button
                  variant="outline"
                  onClick={exportCoupons}
                  className="border-stone-700 text-stone-300"
                  disabled={!data.coupons.coupons?.length}
                >
                  <Download size={16} className="mr-2" />
                  Esporta CSV
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            {data.coupons.stats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                <div className="bg-stone-900/50 border border-stone-800 p-4 rounded-lg text-center">
                  <p className="text-stone-400 text-sm">Totale</p>
                  <p className="text-2xl font-bold text-ivory">{data.coupons.stats.total || 0}</p>
                </div>
                <div className="bg-green-500/10 border border-green-500/30 p-4 rounded-lg text-center">
                  <p className="text-green-400 text-sm">Disponibili</p>
                  <p className="text-2xl font-bold text-green-400">{data.coupons.stats.available || 0}</p>
                </div>
                <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-lg text-center">
                  <p className="text-red-400 text-sm">Usati</p>
                  <p className="text-2xl font-bold text-red-400">{data.coupons.stats.used || 0}</p>
                </div>
                <div className="bg-champagne/10 border border-champagne/30 p-4 rounded-lg text-center">
                  <p className="text-champagne text-sm">50% Disponibili</p>
                  <p className="text-2xl font-bold text-champagne">{data.coupons.stats.available_50_percent || 0}</p>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 p-4 rounded-lg text-center">
                  <p className="text-blue-400 text-sm">30% Disponibili</p>
                  <p className="text-2xl font-bold text-blue-400">{data.coupons.stats.available_30_percent || 0}</p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-4 mb-8">
              <Button
                onClick={generateCoupons}
                disabled={generatingCoupons || (data.coupons.stats?.total > 0)}
                className="bg-green-600 text-white hover:bg-green-700"
              >
                {generatingCoupons ? (
                  <>
                    <RefreshCw size={16} className="mr-2 animate-spin" />
                    Generazione...
                  </>
                ) : (
                  <>
                    <Percent size={16} className="mr-2" />
                    Genera 100 Coupon
                  </>
                )}
              </Button>
              <Button
                onClick={resetCoupons}
                variant="destructive"
                disabled={!data.coupons.stats?.total}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 size={16} className="mr-2" />
                Elimina Tutti
              </Button>
            </div>

            {/* Coupon Table */}
            {loading ? (
              <p className="text-stone-400">Caricamento...</p>
            ) : data.coupons.coupons?.length === 0 ? (
              <div className="bg-stone-900/50 border border-stone-800 p-12 rounded-lg text-center">
                <Tag size={48} className="mx-auto text-stone-600 mb-4" />
                <p className="text-stone-400 mb-4">Nessun coupon presente</p>
                <p className="text-stone-500 text-sm">
                  Clicca su "Genera 100 Coupon" per creare 50 coupon al 50% e 50 al 30%
                </p>
              </div>
            ) : (
              <div className="bg-stone-900/50 border border-stone-800 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-stone-800/50">
                      <tr>
                        <th className="text-left p-4 text-stone-400 font-normal">Codice</th>
                        <th className="text-left p-4 text-stone-400 font-normal">Sconto</th>
                        <th className="text-left p-4 text-stone-400 font-normal">Stato</th>
                        <th className="text-left p-4 text-stone-400 font-normal">Usato Da</th>
                        <th className="text-left p-4 text-stone-400 font-normal">Data Utilizzo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.coupons.coupons.map(coupon => (
                        <tr key={coupon.id} className={`border-t border-stone-800 ${coupon.used ? 'opacity-50' : ''}`}>
                          <td className="p-4 text-ivory font-mono font-bold">{coupon.code}</td>
                          <td className="p-4">
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                              coupon.discount_percent === 50 
                                ? 'bg-champagne/20 text-champagne' 
                                : 'bg-blue-500/20 text-blue-400'
                            }`}>
                              -{coupon.discount_percent}%
                            </span>
                          </td>
                          <td className="p-4">
                            {coupon.used ? (
                              <span className="text-red-400 flex items-center gap-1">
                                <XCircle size={16} /> Usato
                              </span>
                            ) : (
                              <span className="text-green-400 flex items-center gap-1">
                                <CheckCircle size={16} /> Disponibile
                              </span>
                            )}
                          </td>
                          <td className="p-4 text-stone-300 text-sm">{coupon.used_by || '-'}</td>
                          <td className="p-4 text-stone-300 text-sm">{coupon.used_at ? formatDate(coupon.used_at) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
